import React from 'react';
import { Modal, Form, Input, InputNumber, Select, Switch, Button } from 'antd';
import { customBaseUrl, httpPost } from '../../store/http';
import { NotificationManager } from 'react-notifications';

const { Option } = Select;

const CreateCommissionModal = ({ visible, onCancel, onCreate }) => {
    const [form] = Form.useForm();

    const transactionTypes = ['TRANSFER', 'AIRTIME_TOPUP', 'WITHDRAW', 'DATA_TOPUP', 'CABLE', 'UTILITY', 'BETTING'];
    const commissionNames = [
        'SIGNUP_COMMISSION', 'WITHDRAWAL_COMMISSION', 'TRANSFER_COMMISSION', 'AIRTIME_TOPUP_COMMISSION',
        'DATA_TOPUP_COMMISSION', 'CABLE_COMMISSION', 'UTILITY_COMMISSION', 'BETTING_COMMISSION',
        'BILLS_PAYMENT_COMMISSION_AGGREGATOR', 'WITHDRAWAL_COMMISSION_AGGREGATOR', 'POS_COMMISSION',
        'PAYMENT_GATEWAY_COMMISSION'
    ];
    const amountTypes = ['FIXED', 'PERCENTAGE'];
    const customerTypes = ['PERSONAL', 'CORPORATE', 'ALL'];
    const whoToCreditOptions = [
        'REFERRAL', 'CUSTOMER', 'AGGREGATOR_L1', 'AGGREGATOR_L2', 'AGGREGATOR_L3',
        'AGGREGATORS', 'AGGREGATORS_AND_REFERRAL', 'ALL'
    ];

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                isCorporate: values.customerType === 'CORPORATE', // Assuming isCorporate is derived from customerType
                recurrent: values.recurrent || false, // Default to false if not set
                amount: values.amount || 0,
                amountEarnedByWaya: values.amountEarnedByWaya || 0,
                cappedAmount: values.cappedAmount || 0,
                percentageAggregator: values.percentageAggregator || 0,
                percentageAggregatorL1: values.percentageAggregatorL1 || 0,
                percentageAggregatorL2: values.percentageAggregatorL2 || 0,
                percentageAggregatorL3: values.percentageAggregatorL3 || 0,
                percentageCustomer: values.percentageCustomer || 0,
                transactionCount: values.transactionCount || 0,
            };

            const res = await httpPost(`${customBaseUrl.referralUrl}/api/v1/admin/create/commission`, payload);
            if (res.status === 200) {
                NotificationManager.success('Commission created successfully!');
                form.resetFields();
                onCreate(); // Refresh the list
            } else {
                NotificationManager.error(res?.message || 'Failed to create commission.');
            }
        } catch (error) {
            console.error('Failed to create commission:', error);
            NotificationManager.error('An error occurred while creating commission.');
        }
    };

    return (
        <Modal
            visible={visible}
            title="Create New Commission"
            onCancel={onCancel}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" loading={false} onClick={handleOk}>
                    Create
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                name="create_commission_form"
                initialValues={{
                    amountType: 'FIXED',
                    customerType: 'PERSONAL',
                    whoToCredit: 'REFERRAL',
                    recurrent: false,
                    amount: 0,
                    amountEarnedByWaya: 0,
                    cappedAmount: 0,
                    percentageAggregator: 0,
                    percentageAggregatorL1: 0,
                    percentageAggregatorL2: 0,
                    percentageAggregatorL3: 0,
                    percentageCustomer: 0,
                    transactionCount: 0,
                }}
            >
                <Form.Item
                    name="name"
                    label="Commission Name"
                    rules={[{ required: true, message: 'Please select commission name!' }]}
                >
                    <Select placeholder="Select a commission name">
                        {commissionNames.map(name => <Option key={name} value={name}>{name}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="transactionType"
                    label="Transaction Type"
                    rules={[{ required: true, message: 'Please select transaction type!' }]}
                >
                    <Select placeholder="Select a transaction type">
                        {transactionTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="description"
                    label="Description"
                    rules={[{ required: true, message: 'Please enter a description!' }]}
                >
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item
                    name="amountType"
                    label="Amount Type"
                    rules={[{ required: true, message: 'Please select amount type!' }]}
                >
                    <Select placeholder="Select an amount type">
                        {amountTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="amount"
                    label="Amount"
                    rules={[{ required: true, message: 'Please enter amount!' }]}
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="amountEarnedByWaya"
                    label="Amount Earned By Waya"
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="cappedAmount"
                    label="Capped Amount"
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="customerType"
                    label="Customer Type"
                    rules={[{ required: true, message: 'Please select customer type!' }]}
                >
                    <Select placeholder="Select a customer type">
                        {customerTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="percentageAggregator"
                    label="Percentage Aggregator"
                >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                </Form.Item>
                <Form.Item
                    name="percentageAggregatorL1"
                    label="Percentage Aggregator L1"
                >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                </Form.Item>
                <Form.Item
                    name="percentageAggregatorL2"
                    label="Percentage Aggregator L2"
                >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                </Form.Item>
                <Form.Item
                    name="percentageAggregatorL3"
                    label="Percentage Aggregator L3"
                >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                </Form.Item>
                <Form.Item
                    name="percentageCustomer"
                    label="Percentage Customer"
                >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                </Form.Item>
                <Form.Item
                    name="recurrent"
                    label="Recurrent"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="transactionCount"
                    label="Transaction Count"
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="whoToCredit"
                    label="Who To Credit"
                    rules={[{ required: true, message: 'Please select who to credit!' }]}
                >
                    <Select placeholder="Select who to credit">
                        {whoToCreditOptions.map(option => <Option key={option} value={option}>{option}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateCommissionModal;
